import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";

import Spacer from "../../Spacer";
import SignalQuality from "../../icons/SignalQuality";
import LungIcon from "../../icons/LungIcon";
import StackedLineChartIcon from "../../icons/StackedLineChartIcon";
import PoincareChart from "../../charts/PoincareChart";
import PostExertionDecayChart from "../../charts/PostExertionDecayChart";
import PostExertionRespiratorySignalChart from "../../charts/PostExertionRespiratorySignalChart";
import { formatValidatedMeasurement } from "../../../utils/results/validatedRanges";
import { formatRecordingIdDateParts } from "../../../utils/results/recordingIdTime";
import {
  ResultSummaryRow,
  ResultSummarySection,
} from "../ResultSummarySection";
import { trackEvent } from "../../../utils/analytics/track";
import {
  pickPostExertionRecoveryMetrics,
  pickPostExertionDecaySeries,
  pickPostExertionRrMax,
  pickPostExertionRrMin,
  pickPostExertionSignal,
} from "../../../utils/results/postExertion";
import { pickRespiratoryPoincare } from "../../../utils/results/respiratoryPoincare";
import { SHOW_FULL_RESULTS } from "../../../config/results";
import ResultQualityNotice from "../ResultQualityNotice";
import ResultViewScaffold from "../ResultViewScaffold";
import {
  formatPassFailLabel,
  getRespiratoryQualityMessage,
} from "../../../utils/results/resultQuality";
import {
  formatRecoveryDuration,
  formatRecoveryPercent,
  getBreathingVariabilityAxisMax,
} from "../../../utils/results/postExertionView";
import {
  pickRr,
  pickRrSignalQuality,
} from "../../../utils/results/signalQuality";
import { readFiniteNumber } from "../../../utils/results/viewTimeSeries";
import PostExertionCumulativePeakHeightChart from "../../charts/PostExertionCumulativePeakHeightChart";

function PostExertionResultViewImpl({ data, docId, onFinish }) {
  const { t, i18n } = useTranslation();
  const rr = useMemo(() => pickRr(data), [data]);
  const rrMin = useMemo(() => pickPostExertionRrMin(data), [data]);
  const rrMax = useMemo(() => pickPostExertionRrMax(data), [data]);
  const rrSignalQuality = useMemo(() => pickRrSignalQuality(data), [data]);
  const recoveryMetrics = useMemo(
    () => pickPostExertionRecoveryMetrics(data),
    [data],
  );
  const decaySeries = useMemo(() => pickPostExertionDecaySeries(data), [data]);
  const respiratorySignal = useMemo(() => pickPostExertionSignal(data), [data]);
  const respiratoryPoincare = useMemo(
    () => pickRespiratoryPoincare(data),
    [data],
  );
  const respiratoryPoincareAxisMax = useMemo(
    () => getBreathingVariabilityAxisMax(respiratoryPoincare.maxInterval),
    [respiratoryPoincare.maxInterval],
  );

  const rrMsg = useMemo(
    () => getRespiratoryQualityMessage(rrSignalQuality, t),
    [rrSignalQuality, t],
  );
  const respiratoryQualityIsFailing = Boolean(rrMsg);
  const respiratoryQualityLabel = useMemo(
    () => formatPassFailLabel(respiratoryQualityIsFailing, t),
    [respiratoryQualityIsFailing, t],
  );
  const rrDisplay = useMemo(() => formatValidatedMeasurement("rr", rr), [rr]);
  const rrMinDisplay = useMemo(
    () => formatValidatedMeasurement("rr", rrMin),
    [rrMin],
  );
  const rrMaxDisplay = useMemo(
    () => formatValidatedMeasurement("rr", rrMax),
    [rrMax],
  );

  const recordingTime = useMemo(
    () =>
      formatRecordingIdDateParts(
        docId,
        i18n.resolvedLanguage || i18n.language || "en-GB",
      ),
    [docId, i18n.resolvedLanguage, i18n.language],
  );

  const details = SHOW_FULL_RESULTS ? (
    <section className="p-4 flex flex-col gap-4 w-full h-fit">
      <section>
        <h2 className="subtitle-text">
          {t("result.resp.signalChartTitle", "Respiratory Signal")}
        </h2>

        {respiratorySignal.points.length ? (
          <PostExertionRespiratorySignalChart
            detrendedPoints={respiratorySignal.detrendedPoints}
            // smoothedPoints={
            //   respiratorySignal.smoothedPoints.length
            //     ? respiratorySignal.smoothedPoints
            //     : respiratorySignal.points
            // }
            height={220}
            maxSecs={respiratorySignal.maxSecs}
          />
        ) : (
          <div className="muted-text">
            {t("result.resp.noSignal", "No signal trace available.")}
          </div>
        )}
      </section>

      <section>
        <h2 className="subtitle-text">
          {t("result.postExertion.decayChartTitle", "Recovery Decay")}
        </h2>

        {decaySeries.points.length ? (
          <PostExertionDecayChart
            points={decaySeries.points}
            expR2={decaySeries.expR2}
            linearR2={decaySeries.linearR2}
            height={260}
          />
        ) : (
          <div className="muted-text">
            {t(
              "result.postExertion.noDecayData",
              "No recovery decay data available.",
            )}
          </div>
        )}
      </section>

      <section>
        <h2 className="subtitle-text">
          {t(
            "result.resp.breathingVariabilityPoincareTitle",
            "Breathing Variability Poincare",
          )}
        </h2>

        {respiratoryPoincare.x.length && respiratoryPoincare.y.length ? (
          <PoincareChart
            x={respiratoryPoincare.x}
            y={respiratoryPoincare.y}
            height={280}
            initialMode="basic"
            axisMin={0}
            axisMax={respiratoryPoincareAxisMax}
            xTitle={t("result.resp.breathingVariabilityXAxis", "IBI_n (s)")}
            yTitle={t("result.resp.breathingVariabilityYAxis", "IBI_n+1 (s)")}
          />
        ) : (
          <div className="muted-text">
            {t(
              "result.resp.noBreathingVariabilityPoincare",
              "No breathing variability Poincare data available.",
            )}
          </div>
        )}
      </section>
      <section>
        <h2 className="subtitle-text">
          {t(
            "result.postExertion.cumulativePeakHeightChartTitle",
            "Cumulative Peak Height",
          )}
        </h2>

        {respiratorySignal.detrendedPoints.length ? (
          <PostExertionCumulativePeakHeightChart
            detrendedPoints={respiratorySignal.detrendedPoints}
            height={240}
            maxSecs={respiratorySignal.maxSecs}
          />
        ) : (
          <div className="muted-text">
            {t(
              "result.postExertion.noCumulativePeakHeightData",
              "No cumulative peak height data available.",
            )}
          </div>
        )}
      </section>
    </section>
  ) : null;

  return (
    <ResultViewScaffold
      title={t(
        "result.postExertion.completeTitle",
        "Exertion Recovery Recording Complete",
      )}
      recordingTime={recordingTime}
      onFinish={onFinish}
      details={details}
    >
      <Spacer />

      <ResultSummarySection
        icon={<SignalQuality />}
        title={t("result.qualityTitle", "Recording Quality")}
        disabled={respiratoryQualityIsFailing}
      >
        <ResultSummaryRow
          label={t(
            "result.postExertion.respiratorySignalQuality",
            "Respiratory Signal Quality",
          )}
          value={respiratoryQualityLabel}
          disabled={respiratoryQualityIsFailing}
        />

        <ResultQualityNotice
          messages={[rrMsg]}
          linkTo="/guidance/cardiopulmonary"
          ctaLabel={t("result.cardiopulmonary.guidanceCta", "Recording Tips")}
          onLinkClick={() =>
            trackEvent("post_exertion_result_click_guidance", {
              respiratory_quality_failed: respiratoryQualityIsFailing,
            })
          }
        />
      </ResultSummarySection>

      {SHOW_FULL_RESULTS ? (
        <>
          <Spacer />
          <ResultSummarySection
            icon={<LungIcon />}
            title={t("result.tidal.respiratory", "Respiratory")}
            disabled={respiratoryQualityIsFailing}
          >
            <ResultSummaryRow
              label={t("result.postExertion.rrAverageLabel", "RR Avg")}
              value={rrDisplay}
              unit={t("result.resp.rrUnit", "bpm")}
              disabled={respiratoryQualityIsFailing}
            />
            <ResultSummaryRow
              label={t("result.postExertion.rrMinLabel", "RR Min")}
              value={rrMinDisplay}
              unit={t("result.resp.rrUnit", "bpm")}
              disabled={respiratoryQualityIsFailing}
            />
            <ResultSummaryRow
              label={t("result.postExertion.rrMaxLabel", "RR Max")}
              value={rrMaxDisplay}
              unit={t("result.resp.rrUnit", "bpm")}
              disabled={respiratoryQualityIsFailing}
            />
          </ResultSummarySection>
          <Spacer />
          <ResultSummarySection
            icon={<StackedLineChartIcon />}
            title={t("result.postExertion.recoveryTitle", "Recovery")}
          >
            <ResultSummaryRow
              label={t("result.postExertion.rrRecovery60Label", "RR at 60 s")}
              value={formatValidatedMeasurement(
                "rr",
                recoveryMetrics.rrRecovery60,
              )}
              unit={t("result.resp.rrUnit", "bpm")}
            />
            <ResultSummaryRow
              label={t(
                "result.postExertion.recoveryFraction60Label",
                "Recovery at 60 s",
              )}
              value={formatRecoveryPercent(recoveryMetrics.recoveryFraction60)}
              unit={
                readFiniteNumber(recoveryMetrics.recoveryFraction60) != null
                  ? "%"
                  : null
              }
            />
            <ResultSummaryRow
              label={t("result.postExertion.rrRecovery120Label", "RR at 120 s")}
              value={formatValidatedMeasurement(
                "rr",
                recoveryMetrics.rrRecovery120,
              )}
              unit={t("result.resp.rrUnit", "bpm")}
            />
            <ResultSummaryRow
              label={t(
                "result.postExertion.recoveryFraction120Label",
                "Recovery at 120 s",
              )}
              value={formatRecoveryPercent(recoveryMetrics.recoveryFraction120)}
              unit={
                readFiniteNumber(recoveryMetrics.recoveryFraction120) != null
                  ? "%"
                  : null
              }
            />
            <ResultSummaryRow
              label={t(
                "result.postExertion.timeToRecovery90Label",
                "Time to 90% recovery",
              )}
              value={formatRecoveryDuration(recoveryMetrics.timeToRecovery90)}
            />
            <ResultSummaryRow
              label={t(
                "result.postExertion.timeToRecovery95Label",
                "Time to 95% recovery",
              )}
              value={formatRecoveryDuration(recoveryMetrics.timeToRecovery95)}
            />
          </ResultSummarySection>
        </>
      ) : null}
    </ResultViewScaffold>
  );
}

const PostExertionResultView = memo(PostExertionResultViewImpl);

PostExertionResultView.isReady = (data) => pickRr(data) != null;

export default PostExertionResultView;
